import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../routes/paths';
import { convertToVwMob } from '../../Utils/responsive';

const BACK_ICON_SRC = '/Image/posts/backIcon.png';
const HOME_ICON_SRC = '/Image/posts/homeIcon.png';

const PROJECT_PAGE_BASE = 'https://go.missionfund.org';

const sanitizeProjectTitle = (value: string) => {
  return value.replace(/<br\s*\/?>/gi, ' ').replace(/\r?\n/g, ' ');
};

export interface ProjectDetailHeaderProps {
  title: string;
  /** 프로젝트 ID - 홈 아이콘 클릭 시 해당 모금 프로젝트 페이지를 새 창으로 엽니다 */
  projectId?: string;
  onBack?: () => void;
  onHome?: () => void;
  variant?: 'mobile' | 'web';
}

const ProjectDetailHeader: React.FC<ProjectDetailHeaderProps> = ({
  title,
  projectId,
  onBack,
  onHome,
  variant = 'mobile',
}) => {
  const navigate = useNavigate();
  const safeTitle = sanitizeProjectTitle(title);

  const handleBack = onBack ?? (() => navigate(PATHS.PROJECT_MANAGEMENT));
  const handleHome = onHome ?? (() => {
    if (projectId) {
      window.open(`${PROJECT_PAGE_BASE}/${projectId}`, '_blank', 'noopener,noreferrer');
    } else {
      navigate(PATHS.PROJECT_MANAGEMENT);
    }
  });

  const handleHomeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleHome();
  };

  if (variant === 'web') {
    return (
      <WebBackButton>
        <WebBackIconButton type="button" onClick={handleBack} aria-label="뒤로가기">
          <WebBackIcon src={BACK_ICON_SRC} alt="" />
        </WebBackIconButton>
        <WebProjectInfo>
          <WebTitleText>{safeTitle}</WebTitleText>
          <WebHomeIcon src={HOME_ICON_SRC} alt="홈" onClick={handleHomeClick} />
        </WebProjectInfo>
      </WebBackButton>
    );
  }

  return (
    <MobileTopBar>
      <MobileTopLeft type="button" onClick={handleBack} aria-label="back">
        <MobileBackIcon src={BACK_ICON_SRC} alt="" />
      </MobileTopLeft>
      <MobileTitleBox>
        <MobileTopTitle title={safeTitle}>{safeTitle}</MobileTopTitle>
        <MobileHomeIcon src={HOME_ICON_SRC} alt="홈" onClick={handleHomeClick} />
      </MobileTitleBox>
    </MobileTopBar>
  );
};

export default ProjectDetailHeader;

// ─── Mobile styles ─────────────────────────────────────────────────────────
const MobileTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(20)};
`;

const MobileTitleBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
  min-width: 0;
  flex: 1;
`;

const MobileTopLeft = styled.button`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const MobileBackIcon = styled.img`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  object-fit: contain;
`;

const MobileTopTitle = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const MobileHomeIcon = styled.img`
  width: ${convertToVwMob(32)};
  height: ${convertToVwMob(32)};
  object-fit: fill;
  flex-shrink: 0;
  cursor: pointer;
`;

// ─── Web styles ────────────────────────────────────────────────────────────
const WebBackButton = styled.div`
  position: -webkit-sticky;
  position: sticky;
  top: 4.1vw;
  z-index: 100;
  background: ${({ theme }) => theme.colors.grayScale[3]};
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 27px;
  width: calc(100% + 32px);
  min-width: 0;
  padding: 16px 16px 10px 16px;
  margin-left: -16px;
  margin-right: -16px;
  margin-top: -16px;
`;

const WebBackIconButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const WebBackIcon = styled.img`
  width: 9px;
  height: 15px;
  object-fit: contain;
`;

const WebProjectInfo = styled.span`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WebTitleText = styled.span`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WebHomeIcon = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
  cursor: pointer;
  flex-shrink: 0;
`;
